import { Badge, ProductImage, Table, TableBody, TableCell, TableRow } from "randmarcomps";
import { CheckCircle2, CircleHelp, MinusCircle, MoveUpRight, PackageCheck, ShoppingBag, XCircle } from "lucide-react";
import React from "react";
import { FormattedNumber } from "react-intl";
import { Link, useParams } from "react-router-dom";
import { Product as RTKProduct } from "randmar-api-client";
import Path from "../../routes/Path";
import { Product } from "../../stores/product/Product";
import { convertLegacyProductToRTK } from "../../utils/converters";
import CurrencyUtils from "../util/CurrencyUtils";

// Type guard to check if the product is the legacy MobX type
function isLegacyProduct(product: Product | RTKProduct): product is Product {
    // A simple way to check is to see if a property exists on the legacy model that doesn't on the new one.
    // `source` is a good candidate. Or we can check the instance.
    return product instanceof Product || "source" in product;
}

// Props definition allowing either legacy or new product type
interface IProps {
  product: Product | RTKProduct;
}

type BadgeVariant = React.ComponentProps<typeof Badge>["variant"];

const stateBadge = (state?: string | null): { variant: BadgeVariant; icon: React.ReactNode; label: string } => {
  const trimmed = state?.trim() || "";
  const normalized = trimmed.toLowerCase();
  if (normalized === "active") return { variant: "success", icon: <CheckCircle2 className="h-4 w-4" aria-hidden="true" />, label: trimmed };
  if (normalized === "discontinued") return { variant: "destructive", icon: <XCircle className="h-4 w-4" aria-hidden="true" />, label: trimmed };
  if (normalized === "hold") return { variant: "warning", icon: <MinusCircle className="h-4 w-4" aria-hidden="true" />, label: trimmed };
  if (!trimmed) return { variant: "outline", icon: <CircleHelp className="h-4 w-4" aria-hidden="true" />, label: "—" };
  return { variant: "secondary", icon: <CircleHelp className="h-4 w-4" aria-hidden="true" />, label: trimmed };
};

const ProductGeneral: React.FC<IProps> = ({ product: productFromProps }) => {
  const { applicationId } = useParams<{ applicationId: string }>();

  // Convert legacy product to new RTK type if necessary
  const product = isLegacyProduct(productFromProps)
    ? convertLegacyProductToRTK(productFromProps)
    : productFromProps;
  const resolvedState = stateBadge(product.State);
  const isShopifyReady = Boolean(product.BodyHTML && product.Title && product.MAP);

  return (
    <>
      <div className="m-6 flex flex-col items-center justify-between gap-6 lg:flex-row">
        <div className="flex w-full flex-col items-center justify-center gap-3">
          <h2 className="text-xl font-semibold text-foreground text-center">{product.Title}</h2>

          <ProductImage randmarSKU={product.RandmarSKU || ""} />

          <div className="flex flex-row items-center justify-center gap-2">
            <Badge variant={resolvedState.variant} size="sm" icon={resolvedState.icon} title={product.State ?? undefined}>
              {resolvedState.label}
            </Badge>
            <Badge
              variant={product.ManufacturerCategory?.QualificationRequired ? "warning" : "secondary"}
              size="sm"
              icon={<PackageCheck className="h-4 w-4" aria-hidden="true" />}
              title="Qualification"
            >
              {product.ManufacturerCategory?.QualificationRequired ? "Qualification required" : "No qualification"}
            </Badge>
          </div>
          <div className="flex flex-row items-center justify-center gap-2">
            <Badge
              variant={isShopifyReady ? "success" : "destructive"}
              size="sm"
              icon={<ShoppingBag className="h-4 w-4" aria-hidden="true" />}
              title="Shopify integration readiness"
            >
              {isShopifyReady ? "Shopify ready" : "Not Shopify ready"}
            </Badge>
            <Badge
              variant={product.AutoUpdate ? "success" : "secondary"}
              size="sm"
              icon={<MoveUpRight className="h-4 w-4" aria-hidden="true" />}
              title="Reorder behavior"
            >
              {product.AutoUpdate ? "Automatic reorder" : "Manual reorder"} ({product.MasterCarton})
            </Badge>
          </div>

          <p className="text-xs font-semibold text-muted-foreground">Standard cost</p>
          <p className="text-3xl font-semibold text-foreground">
            <FormattedNumber value={product.Distribution?.Cost || 0} {...CurrencyUtils.format} />
          </p>
        </div>

        <div className="flex w-full flex-col items-center justify-center gap-2">
          <div className="w-full overflow-hidden rounded-2xl border border-border bg-card text-center shadow-sm">
            <Table>
              <TableBody>
                <TableRow className="hover:bg-muted/50">
                  <TableCell className="font-semibold">MPN</TableCell>
                  <TableCell>{product.MPN}</TableCell>
                </TableRow>
                <TableRow className="hover:bg-muted/50">
                  <TableCell className="font-semibold">Manufacturer</TableCell>
                  <TableCell>{product.ManufacturerName}</TableCell>
                </TableRow>
                <TableRow className="hover:bg-muted/50">
                  <TableCell className="font-semibold">Category</TableCell>
                  <TableCell>
                    {product.ManufacturerCategory ? (
                      product.IsManufacturer ? (
                        <Link
                          to={Path.category(applicationId!, product.ManufacturerCategory?.CategoryCode ?? undefined)}
                          className="font-medium underline underline-offset-2"
                        >
                          {product.ManufacturerCategory?.CategoryName}
                        </Link>
                      ) : (
                        <>{product.ManufacturerCategory?.CategoryName}</>
                      )
                    ) : (
                      <>{product.ProductType}</>
                    )}
                  </TableCell>
                </TableRow>
                {product.UPC ? (
                  <TableRow className="hover:bg-muted/50">
                    <TableCell className="font-semibold">UPC</TableCell>
                    <TableCell>{product.UPC}</TableCell>
                  </TableRow>
                ) : null}
                {product.MAP ? (
                  <TableRow className="hover:bg-muted/50">
                    <TableCell className="font-semibold" title="Minimum advertised price">
                      MAP
                    </TableCell>
                    <TableCell>
                      <FormattedNumber value={product.MAP} {...CurrencyUtils.format} />
                    </TableCell>
                  </TableRow>
                ) : null}
                {product.CreatedBy ? (
                  <TableRow className="hover:bg-muted/50">
                    <TableCell className="font-semibold">Created by</TableCell>
                    <TableCell>{product.CreatedBy}</TableCell>
                  </TableRow>
                ) : null}
                <TableRow className="hover:bg-muted/50">
                  <TableCell className="font-semibold">Unit weight</TableCell>
                  <TableCell>{product.UnitWeight === 0 ? "Unset" : `${product.UnitWeight} lbs`}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProductGeneral;
